import React, { useState, useEffect, useRef } from 'react';
import { getPlantCodes } from '../../../services/plantSafetyWorkPermitService';
import { addTestSanction, updateTestSanction } from '../../../services/lvWorkPermitFlowService';
import { X, Info } from 'lucide-react';
import * as Yup from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Props {
  open: boolean;
  onClose: () => void;
  permitData: any;
  onSubmit: () => void;
  initialData?: any;
}

interface PlantCode {
  id: number;
  title: string;
}

const steps = ['Sanction Details', 'Other Details', 'Preview & Save'];

// Strips HTML tags so an "empty" Quill editor (<p><br></p>) doesn't pass required validation
const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

// Forces safe target/rel on rendered links to prevent reverse tabnabbing
const sanitizeLinks = (html: string) => (html || '').replace(/<a /g, '<a target="_blank" rel="noopener noreferrer" ');

// Reusable Tailwind classes that restyle raw HTML injected via dangerouslySetInnerHTML.
// Keep this string identical wherever rich text is rendered (here and in SanctionContent.tsx)
// so lists/links/headings look the same everywhere.
export const richTextDisplayClasses =
  'text-sm [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2 ' +
  '[&_li]:mb-1 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_a]:text-blue-600 [&_a]:underline ' +
  '[&_h2]:text-lg [&_h2]:font-semibold [&_h2]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline';

const RichTextDisplay = ({ html, fallback = '' }: { html?: string; fallback?: string }) => (
  <div className={richTextDisplayClasses} dangerouslySetInnerHTML={{ __html: sanitizeLinks(html || '') || fallback }} />
);

const sanctionSchema = Yup.object().shape({
  selectedPlantCode: Yup.string().required('Plant is required'),

  jobNumber: Yup.string().trim().required('Job Number is required'),

  workToBeCarriedOut: Yup.string().test('not-empty', 'Work To Be Carried Out is required', (val) => !!val && stripHtml(val).length > 0),

  workPermissible: Yup.string().test(
    'not-empty',
    'Work Permissible Under This Sanction For Test is required',
    (val) => !!val && stripHtml(val).length > 0,
  ),

  initiateSignal: Yup.string().test('not-empty', 'Initiate Signal is required', (val) => !!val && stripHtml(val).length > 0),

  haltSignal: Yup.string().test('not-empty', 'Halt Signal is required', (val) => !!val && stripHtml(val).length > 0),

  isolationRequest: Yup.string().test('not-empty', 'Isolation Request is required', (val) => !!val && stripHtml(val).length > 0),
});

interface RichTextFieldProps {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
}

const quillModules = {
  toolbar: {
    container: [[{ header: [2, false] }], ['bold', 'italic', 'underline'], ['link'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean']],
    handlers: {
      link: function (this: any, value: boolean) {
        const quillEditor = this.quill;
        const range = quillEditor.getSelection();

        if (!value) {
          quillEditor.format('link', false);
          return;
        }

        if (!range || range.length === 0) {
          alert('Please select the text you want to turn into a link.');
          return;
        }

        const existingLink = quillEditor.getFormat(range).link;
        const url = window.prompt('Enter a URL', existingLink || 'https://');

        if (url === null) return; // cancelled

        if (url.trim() === '') {
          quillEditor.format('link', false);
          return;
        }

        const isValidUrl = /^(https?:\/\/|\/)/i.test(url.trim());
        if (!isValidUrl) {
          alert('Please enter a valid URL starting with http:// or https://');
          return;
        }

        quillEditor.format('link', url.trim());
      },
    },
  },
};

const quillFormats = ['header', 'bold', 'italic', 'underline', 'link', 'list', 'bullet'];

const RichTextField = ({ value, onChange, placeholder = 'Add description here', disabled = false, error = false }: RichTextFieldProps) => {
  const quillRef = useRef<ReactQuill>(null);

  return (
    <div className={`rounded-md bg-white transition-colors ${error ? 'border border-red-500' : 'border border-gray-300'}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        readOnly={disabled}
        placeholder={placeholder}
        modules={quillModules}
        formats={quillFormats}
        className={disabled ? 'bg-gray-50' : ''}
      />
    </div>
  );
};

const LabelWithInfo = ({ text }: { text: string }) => (
  <div className="flex items-center gap-2 mb-2">
    <label className="text-sm font-medium">{text}</label>
    <Info size={14} className="text-gray-400" />
  </div>
);

const SanctionForTestModal: React.FC<Props> = ({ open, onClose, permitData, onSubmit, initialData }) => {
  const isEditing = Boolean(initialData?.id);
  const [isHydrated, setIsHydrated] = useState(false);
  const [errors, setErrors] = useState({
    selectedPlantCode: '',
    jobNumber: '',
    workToBeCarriedOut: '',
    workPermissible: '',
    initiateSignal: '',
    haltSignal: '',
    isolationRequest: '',
  });
  const [currentStep, setCurrentStep] = useState(0);

  const [plantCodes, setPlantCodes] = useState<PlantCode[]>([]);
  const [selectedPlantCode, setSelectedPlantCode] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [loadingPlantCodes, setLoadingPlantCodes] = useState(true);

  const [formData, setFormData] = useState({
    plant: '',
    jobNumber: '',
    workToBeCarriedOut: '',
    workPermissible: '',
    initiateSignal: '',
    haltSignal: '',
    isolationRequest: '',
  });

  useEffect(() => {
    if (!open) {
      // The component remains mounted while hidden. Mark it unhydrated so
      // rich-text editors cannot mount with the cleared form on the next open.
      setIsHydrated(false);
      return;
    }

    const permitJobNumber = String(permitData?.job_number ?? initialData?.job_number ?? '');

    setCurrentStep(0);
    setSelectedPlantCode(initialData?.plant_code_id ? String(initialData.plant_code_id) : '');
    setJobNumber(permitJobNumber);
    setFormData({
      plant: initialData?.plant_code_id ? String(initialData.plant_code_id) : '',
      jobNumber: permitJobNumber,
      workToBeCarriedOut: initialData?.work || '',
      workPermissible: initialData?.allowed_work || '',
      initiateSignal: initialData?.init_signal || '',
      haltSignal: initialData?.halt_signal || '',
      isolationRequest: initialData?.isolation_signal || initialData?.isolation || '',
    });
    setErrors({
      selectedPlantCode: '',
      jobNumber: '',
      workToBeCarriedOut: '',
      workPermissible: '',
      initiateSignal: '',
      haltSignal: '',
      isolationRequest: '',
    });
    setIsHydrated(true);
  }, [open, initialData, permitData?.job_number]);

  useEffect(() => {
    const fetchPlantCodes = async () => {
      try {
        setLoadingPlantCodes(true);
        const response = await getPlantCodes();
        const data = Array.isArray(response) ? response : response?.data || [];
        setPlantCodes(data);
      } catch (error) {
        console.error('Error loading plant codes', error);
      } finally {
        setLoadingPlantCodes(false);
      }
    };

    fetchPlantCodes();
  }, []);

  // ReactQuill must receive saved values on its first mounted render. Without
  // this gate, the first edit open mounts blank editors before the effect above
  // copies initialData, while a second open appears to work from retained state.
  if (!open || !isHydrated) return null;

  const nextStep = async () => {
    if (currentStep === 0) {
      try {
        await Yup.object({
          selectedPlantCode: sanctionSchema.fields.selectedPlantCode,
          jobNumber: sanctionSchema.fields.jobNumber,
          workToBeCarriedOut: sanctionSchema.fields.workToBeCarriedOut,
          workPermissible: sanctionSchema.fields.workPermissible,
        }).validate(
          {
            selectedPlantCode,
            jobNumber,
            workToBeCarriedOut: formData.workToBeCarriedOut,
            workPermissible: formData.workPermissible,
          },
          { abortEarly: false },
        );

        setErrors({
          selectedPlantCode: '',
          jobNumber: '',
          workToBeCarriedOut: '',
          workPermissible: '',
          initiateSignal: '',
          haltSignal: '',
          isolationRequest: '',
        });

        setCurrentStep(1);
      } catch (err: any) {
        const validationErrors: any = {};

        err.inner.forEach((error: any) => {
          validationErrors[error.path] = error.message;
        });

        setErrors((prev) => ({
          ...prev,
          ...validationErrors,
        }));
      }

      return;
    }

    if (currentStep === 1) {
      try {
        await Yup.object({
          initiateSignal: sanctionSchema.fields.initiateSignal,
          haltSignal: sanctionSchema.fields.haltSignal,
          isolationRequest: sanctionSchema.fields.isolationRequest,
        }).validate(
          {
            initiateSignal: formData.initiateSignal,
            haltSignal: formData.haltSignal,
            isolationRequest: formData.isolationRequest,
          },
          { abortEarly: false },
        );

        setErrors((prev) => ({
          ...prev,
          initiateSignal: '',
          haltSignal: '',
          isolationRequest: '',
        }));

        setCurrentStep(2);
      } catch (err: any) {
        const validationErrors: any = {};

        err.inner.forEach((error: any) => {
          validationErrors[error.path] = error.message;
        });

        setErrors((prev) => ({
          ...prev,
          ...validationErrors,
        }));
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const handleIssuePermit = async () => {
    try {
      await sanctionSchema.validate(
        {
          selectedPlantCode,
          jobNumber,
          workToBeCarriedOut: formData.workToBeCarriedOut,
          workPermissible: formData.workPermissible,
          initiateSignal: formData.initiateSignal,
          haltSignal: formData.haltSignal,
          isolationRequest: formData.isolationRequest,
        },
        { abortEarly: false },
      );

      // clear all validation errors
      setErrors({
        selectedPlantCode: '',
        jobNumber: '',
        workToBeCarriedOut: '',
        workPermissible: '',
        initiateSignal: '',
        haltSignal: '',
        isolationRequest: '',
      });

      const trimmedJobNumber = jobNumber.trim();

      const payload = {
        plant_code_id: Number(selectedPlantCode),
        job_number: trimmedJobNumber,
        work: formData.workToBeCarriedOut,
        allowed_work: formData.workPermissible,
        isolation: formData.isolationRequest,
        init_signal: formData.initiateSignal,
        halt_signal: formData.haltSignal,
        isolation_signal: formData.isolationRequest,
      };

      const permitId = permitData?.id || permitData?.permitId;

      if (isEditing) {
        await updateTestSanction(permitId, payload);
      } else {
        await addTestSanction(permitId, payload);
      }

      onSubmit?.();
      resetForm();
      onClose();
    } catch (error: any) {
      if (error?.inner) {
        const validationErrors: any = {};

        error.inner.forEach((err: any) => {
          validationErrors[err.path] = err.message;
        });

        setErrors((prev) => ({
          ...prev,
          ...validationErrors,
        }));

        return;
      }

      console.error(`Failed to ${isEditing ? 'update' : 'add'} test sanction:`, error);

      if (error?.response?.data) {
        alert(error.response.data.message || `Failed to ${isEditing ? 'update' : 'issue'} sanction`);
      }
    }
  };

  const resetForm = () => {
    setCurrentStep(0);
    setSelectedPlantCode('');
    setJobNumber('');

    setErrors({
      selectedPlantCode: '',
      jobNumber: '',
      workToBeCarriedOut: '',
      workPermissible: '',
      initiateSignal: '',
      haltSignal: '',
      isolationRequest: '',
    });

    setFormData({
      plant: '',
      jobNumber: '',
      workToBeCarriedOut: '',
      workPermissible: '',
      initiateSignal: '',
      haltSignal: '',
      isolationRequest: '',
    });
  };

  const selectedPlant = plantCodes.find((p) => p.id.toString() === selectedPlantCode)?.title;

  return (
    <div className="fixed inset-0 z-[999] bg-black/40 flex justify-center items-center">
      <div className="bg-white w-[1200px] h-[85vh] rounded-lg shadow-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="font-semibold text-lg">{isEditing ? 'Edit Sanction for Test' : 'Sanction for Test'}</h2>
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-16 py-6">
          <div className="max-w-[500px] mx-auto">
            <div className="flex items-center justify-between relative">
              <div
                className="absolute top-[6px] h-[2px] bg-gray-300"
                style={{
                  left: `${100 / steps.length / 2}%`,
                  right: `${100 / steps.length / 2}%`,
                }}
              />

              <div
                className="absolute top-[6px] h-[2px] bg-green-500 transition-all duration-300"
                style={{
                  left: `${100 / steps.length / 2}%`,
                  width: `calc(
                   (100% - ${100 / steps.length}%)
                   * ${currentStep / (steps.length - 1)}
                  )`,
                }}
              />

              {steps.map((step, index) => {
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;

                return (
                  <div key={index} className="flex flex-col items-center relative z-10" style={{ width: `${100 / steps.length}%` }}>
                    <div
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        isActive || isCompleted ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                    <span
                      className={`mt-3 text-xs font-medium text-center ${isActive || isCompleted ? 'text-green-600' : 'text-gray-400'}`}
                    >
                      {step}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-10 pb-10">
          {/* STEP 1 */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium">
                    Plant <span className="text-red-500">*</span>
                  </label>

                  <select
                    value={selectedPlantCode}
                    onChange={(e) => {
                      setSelectedPlantCode(e.target.value);

                      setErrors((prev) => ({
                        ...prev,
                        selectedPlantCode: '',
                      }));
                    }}
                    className={`w-full mt-2 border rounded-md p-3 ${errors.selectedPlantCode ? 'border-red-500' : ''}`}
                  >
                    <option value="">{loadingPlantCodes ? 'Loading Plant Codes...' : 'Select Plant'}</option>

                    {plantCodes.map((plant) => (
                      <option key={plant.id} value={plant.id}>
                        {plant.title}
                      </option>
                    ))}
                  </select>

                  {errors.selectedPlantCode && <p className="text-red-500 text-sm mt-1">{errors.selectedPlantCode}</p>}
                </div>
                <div>
                  <label className="text-sm font-medium">
                    Job Number <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    value={jobNumber}
                    disabled
                    className={`w-full mt-2 border rounded-md bg-gray-100 p-3 text-gray-600 cursor-not-allowed ${
                      errors.jobNumber ? 'border-red-500' : ''
                    }`}
                  />

                  {errors.jobNumber && <p className="text-red-500 text-sm mt-1">{errors.jobNumber}</p>}
                </div>
              </div>

              <div>
                <div className="flex items-center">
                  <LabelWithInfo text="Work To Be Carried Out" />
                  <span className="text-red-500 ml-1">*</span>
                </div>
                <RichTextField
                  value={formData.workToBeCarriedOut}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      workToBeCarriedOut: val,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      workToBeCarriedOut: '',
                    }));
                  }}
                  error={!!errors.workToBeCarriedOut}
                />

                {errors.workToBeCarriedOut && <p className="text-red-500 text-sm mt-1">{errors.workToBeCarriedOut}</p>}
              </div>

              <div>
                <div className="flex items-center">
                  <LabelWithInfo text="Work Permissible Under This Sanction For Test" />
                  <span className="text-red-500 ml-1">*</span>
                </div>
                <RichTextField
                  value={formData.workPermissible}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      workPermissible: val,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      workPermissible: '',
                    }));
                  }}
                  error={!!errors.workPermissible}
                />

                {errors.workPermissible && <p className="text-red-500 text-sm mt-1">{errors.workPermissible}</p>}
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Please describe the signal or procedure that will initiate the test. If it involves a verbal instruction, provide the
                  specific wording to be used.
                  <span className="text-red-500">*</span>
                </label>
                <RichTextField
                  value={formData.initiateSignal}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      initiateSignal: val,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      initiateSignal: '',
                    }));
                  }}
                  error={!!errors.initiateSignal}
                />

                {errors.initiateSignal && <p className="text-red-500 text-sm mt-1">{errors.initiateSignal}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Please describe the signal or procedure that will halt the test. If it involves a verbal instruction, provide the specific
                  wording to be used.
                  <span className="text-red-500">*</span>
                </label>
                <RichTextField
                  value={formData.haltSignal}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      haltSignal: val,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      haltSignal: '',
                    }));
                  }}
                  error={!!errors.haltSignal}
                />

                {errors.haltSignal && <p className="text-red-500 text-sm mt-1">{errors.haltSignal}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Please describe how isolation is requested during the test. If it involves a verbal request, provide the specific wording
                  to be used.
                  <span className="text-red-500">*</span>
                </label>
                <RichTextField
                  value={formData.isolationRequest}
                  onChange={(val) => {
                    setFormData((prev) => ({
                      ...prev,
                      isolationRequest: val,
                    }));

                    setErrors((prev) => ({
                      ...prev,
                      isolationRequest: '',
                    }));
                  }}
                  error={!!errors.isolationRequest}
                />

                {errors.isolationRequest && <p className="text-red-500 text-sm mt-1">{errors.isolationRequest}</p>}
              </div>
            </div>
          )}

          {/* STEP 3 - PREVIEW & SAVE */}
          {currentStep === 2 && (
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Sanction Details</h3>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-500">Plant</p>
                        <p className="font-medium">{selectedPlant || 'Not Selected'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Job Number</p>
                        <p className="font-medium">{jobNumber || 'Not Provided'}</p>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Work To Be Carried Out</p>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <RichTextDisplay html={formData.workToBeCarriedOut} fallback="No details provided" />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Work Permissible Under This Sanction For Test</p>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <RichTextDisplay html={formData.workPermissible} fallback="No details provided" />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Other Details</h3>
                  <div className="space-y-6">
                    <div>
                      <p className="text-sm text-gray-500 mb-2">Signal/Procedure to Initiate the Test</p>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <RichTextDisplay html={formData.initiateSignal} fallback="Not provided" />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Signal/Procedure to Halt the Test</p>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <RichTextDisplay html={formData.haltSignal} fallback="Not provided" />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">How Isolation is Requested</p>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <RichTextDisplay html={formData.isolationRequest} fallback="Not provided" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-5 flex justify-end gap-3">
          <button
            onClick={() => {
              resetForm();
              onClose();
            }}
            className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>

          {currentStep > 0 && (
            <button onClick={prevStep} className="px-6 py-2 border rounded-md">
              Back
            </button>
          )}

          {currentStep < 2 ? (
            <button onClick={nextStep} className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
              Next
            </button>
          ) : (
            <button onClick={handleIssuePermit} className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
              {isEditing ? 'Update' : 'Issue Permit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SanctionForTestModal;
